// Keyboard and touch. Movement is a held state; everything else is an edge.
//
// The on-screen pad is a second source of the same held state: `setTouch`
// records where the thumb is, and the keyboard wins whenever a key is held.
//
// The chat line is a real <input> in the HUD now, so this no longer owns a
// hidden field or a "chatting" flag to keep in step with one. It has one rule
// instead: a key that arrives from somewhere that takes typing is not a game
// key. The composer stops the bubble at its own node and `fromField` catches
// anything else — a landing form, a future panel — that ever sits over the
// canvas.

export interface Intent {
  mx: number;
  my: number;
}

export interface InputHandlers {
  move(intent: Intent): void;
  attack(): void;
  buy(): void;
  openChat(): void;
}

const MOVE_KEYS: Record<string, [number, number]> = {
  KeyW: [0, -1],
  ArrowUp: [0, -1],
  KeyS: [0, 1],
  ArrowDown: [0, 1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};

/** Whether the key belongs to something that takes typing. */
function fromField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export class Input {
  private held = new Set<string>();
  private touch: Intent = { mx: 0, my: 0 };
  private handlers: InputHandlers;
  private readonly onKeyDownBound = (ev: KeyboardEvent): void => this.onKeyDown(ev);
  private readonly onKeyUpBound = (ev: KeyboardEvent): void => {
    if (this.held.delete(ev.code)) this.emitIntent();
  };
  // A window that loses focus keeps no keys: alt-tabbing away mid-stride used
  // to leave a body walking into a wall until you came back.
  private readonly onBlurBound = (): void => this.release();

  constructor(handlers: InputHandlers) {
    this.handlers = handlers;
    window.addEventListener("keydown", this.onKeyDownBound);
    window.addEventListener("keyup", this.onKeyUpBound);
    window.addEventListener("blur", this.onBlurBound);
  }

  /** Drop every held key and say so. Used when focus leaves and when the chat
   *  line takes the keyboard, so a body does not keep walking while you type. */
  release(): void {
    if (this.held.size === 0 && this.touch.mx === 0 && this.touch.my === 0) return;
    this.held.clear();
    this.touch = { mx: 0, my: 0 };
    this.emitIntent();
  }

  /** The direction the on-screen pad is held in, or zero when it is let go. */
  setTouch(intent: Intent): void {
    if (intent.mx === this.touch.mx && intent.my === this.touch.my) return;
    this.touch = { mx: intent.mx, my: intent.my };
    this.emitIntent();
  }

  stop(): void {
    window.removeEventListener("keydown", this.onKeyDownBound);
    window.removeEventListener("keyup", this.onKeyUpBound);
    window.removeEventListener("blur", this.onBlurBound);
  }

  private onKeyDown(ev: KeyboardEvent): void {
    if (fromField(ev.target)) return;

    if (ev.code === "Enter") {
      ev.preventDefault();
      this.release();
      this.handlers.openChat();
      return;
    }
    if (ev.code === "Space") {
      ev.preventDefault();
      this.handlers.attack();
      return;
    }
    if (ev.code === "KeyE") {
      ev.preventDefault();
      this.handlers.buy();
      return;
    }
    if (MOVE_KEYS[ev.code]) {
      ev.preventDefault();
      const before = this.held.size;
      this.held.add(ev.code);
      if (this.held.size !== before) this.emitIntent();
    }
  }

  /** The current movement intent. Later presses win on an axis, so rolling from
   *  one key to its opposite turns round instead of stopping. */
  intent(): Intent {
    if (this.held.size === 0) return { ...this.touch };
    let mx = 0;
    let my = 0;
    for (const code of this.held) {
      const vec = MOVE_KEYS[code];
      if (!vec) continue;
      if (vec[0] !== 0) mx = vec[0];
      if (vec[1] !== 0) my = vec[1];
    }
    return { mx, my };
  }

  private emitIntent(): void {
    this.handlers.move(this.intent());
  }
}
